import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import {
  NetsuiteCreditMemoSources,
  NetSuiteItemReceiptRestockDecisionSourceZodSchema,
  NetSuiteRecordCurrencyZodSchema,
  NetsuiteRmaSources,
} from "@redotech/redo-return-core-common/integrations/returns/netsuite/netsuite";
import { NetSuiteTransactionTypeZodSchema } from "@redotech/redo-return-core-common/return-integration-types";
import * as z from "zod";
import { array, boolean, object, string, enum as zEnum } from "zod";
import {
  netsuiteCashRefundCustomizationSchema,
  netsuiteCreditMemoCustomizationSchema,
  netSuiteCustomDocumentFieldSchema,
  netsuiteNewOrderSettingsSchema,
  netsuiteProcessingCustomFieldSchema,
  netsuiteRMACustomizationSchema,
} from "./shared-schemas";

const returnReasonSchema = object({ reason: string(), refName: string() });

const netsuiteReturnSchema = object({
  location: string().optional(),
  reasonDefault: string().optional(),
  reasons: array(returnReasonSchema).optional(),
});

const itemReceiptRestockLocationMappingSchema = object({
  redoLocationId: string(),
  netsuiteLocationId: string(),
  netsuiteLocationName: string().optional(),
  bins: array(
    object({
      binId: string(),
      binName: string().optional(),
      inventoryStatusId: string().optional(),
      inventoryStatusName: string().optional(),
    }),
  ).optional(),
});

const itemReceiptRestockDefaultSchema = object({
  netsuiteLocationId: string(),
  netsuiteLocationName: string().optional(),
  bins: array(
    object({
      binId: string(),
      binName: string().optional(),
      inventoryStatusId: string().optional(),
      inventoryStatusName: string().optional(),
    }),
  ).optional(),
});

const netsuiteItemReceiptSchema = object({
  createItemReceipt: boolean().optional(),
  processReturnsEnabled: boolean().optional(),
  customFormId: string().optional(),
  restockDecisionSource:
    NetSuiteItemReceiptRestockDecisionSourceZodSchema.optional(),
  restockByLocationEnabled: boolean().optional(),
  restockLocationDefault: itemReceiptRestockDefaultSchema.optional(),
  restockLocationMappings: array(
    itemReceiptRestockLocationMappingSchema,
  ).optional(),
  processingCustomFields: array(netsuiteProcessingCustomFieldSchema).optional(),
});

const netsuiteCashRefundSchema = object({
  createCashRefund: boolean().optional(),
  returnTypes: array(zEnum(CompensationMethod)).optional(),
  cashRefundCustomizations: netsuiteCashRefundCustomizationSchema.optional(),
});

const netsuiteCreditMemoSchema = object({
  createCreditMemo: boolean().optional(),
  customFormId: string().optional(),
  sources: array(zEnum(NetsuiteCreditMemoSources)).optional(),
  allowedCompensationMethods: array(zEnum(CompensationMethod)).optional(),
  creditMemoCustomizations: netsuiteCreditMemoCustomizationSchema.optional(),
});

const returnTypeEnum = zEnum([
  "return",
  "claim",
  "warranty",
  "recycle",
  "withdrawal",
]);

const netsuiteReturnAuthorizationSchema_ = object({
  createReturnAuthorization: boolean().optional(),
  sourcePriority: array(zEnum(NetsuiteRmaSources)).optional(),
  createReturnAuthorizationReturnTypes: array(returnTypeEnum).optional(),
  closeReturnAuthorization: object({
    enabled: boolean().optional(),
    returnTypes: array(returnTypeEnum).optional(),
    permissionList: array(zEnum(["close", "automation"])).optional(),
  }).optional(),
  includeGreenItems: boolean().optional(),
  productReturnReasonField: string().optional(),
  allowFullGreenReturns: boolean().optional(),
  allowInStoreReturns: boolean().optional(),
  allowThirdPartyReturns: boolean().optional(),
  autoApproveReturnAuthorization: boolean().optional(),
  trackingNumberRequired: boolean().optional(),
  taxInclusivePricing: boolean().optional(),
  shipmentSettings: object({
    shouldUpdateShipment: boolean().optional(),
    shipmentStatusField: string().optional(),
    shipmentDateField: string().optional(),
    shipmentTrackerNumberField: string().optional(),
  }).optional(),
});

const netsuiteVendorReturnAuthorizationSchema = object({
  enabled: boolean().optional(),
  closeVendorReturnAuthorization: object({
    enabled: boolean().optional(),
    returnTypes: array(returnTypeEnum).optional(),
  }).optional(),
});
const netsuiteReturnAuthorizationSchema: z.ZodType<NetsuiteReturnAuthorization> =
  netsuiteReturnAuthorizationSchema_;
export interface NetsuiteReturnAuthorization extends z.infer<
  typeof netsuiteReturnAuthorizationSchema_
> {}

const netsuiteCustomerRefundSchema = object({
  accountId: string().optional(),
  createCustomerRefund: boolean().optional(),
  customFormId: string().optional(),
  orderIdField: string().optional(),
  customFieldsOnCustomerRefund: array(
    netSuiteCustomDocumentFieldSchema,
  ).optional(),
  allowedCompensationMethods: array(zEnum(CompensationMethod)).optional(),
});

const netsuiteSupportCaseSchema = object({
  createSupportCasesForWarranties: boolean().optional(),
});

const orderNameModificationSchema = object({
  match: string(),
  replace: string(),
});

export const getNetSuiteIntegration = {
  name: "getNetSuiteIntegration" as const,
  input: object({}),
  output: object({
    netsuiteIntegration: object({
      accountId: string(),
      authMethod: zEnum(["tba", "oauth2"]).optional(),
      cashRefund: netsuiteCashRefundSchema.optional(),
      creditMemo: netsuiteCreditMemoSchema.optional(),
      returnAuthorization: netsuiteReturnAuthorizationSchema.optional(),
      itemReceipt: netsuiteItemReceiptSchema.optional(),
      customerRefund: netsuiteCustomerRefundSchema.optional(),
      clientId: string().optional(),
      clientSecret: string().optional(),
      token: string().optional(),
      tokenSecret: string().optional(),
      exchangeOrderSettings: netsuiteNewOrderSettingsSchema.optional(),
      exchangedItemField: string().optional(),
      itemReceiptSkuField: string().optional(),
      itemValue: zEnum([
        "credit",
        "refund",
        "total",
        "total-pre-discount",
      ]).optional(),
      netSuiteRecordCurrency: NetSuiteRecordCurrencyZodSchema.optional(),
      overrideExchangeRateWithShopifyRate: boolean().optional(),
      discountInventoryItemId: string().optional(),
      useDiscountLocationId: boolean().optional(),
      orderNameModification: orderNameModificationSchema.optional(),
      redoReturnIdField: string().optional(),
      returns: netsuiteReturnSchema.optional(),
      warranties: boolean().optional(),
      supportCase: netsuiteSupportCaseSchema.optional(),
      vendorReturnAuthorization:
        netsuiteVendorReturnAuthorizationSchema.optional(),
      salesTaxItemId: string().optional(),
      shopifyOrderNameField: string().optional(),
      skuField: string().optional(),
      taxMode: zEnum(["celigo", "override"]),
      trackingCodeField: string().optional(),
      transactionType: NetSuiteTransactionTypeZodSchema,
      RMACustomization: netsuiteRMACustomizationSchema.optional(),
      syncOrders: object({
        enabled: boolean(),
        orderMappingArtifactId: string().optional(),
      }).optional(),
    }).optional(),
  }),
};
